import React, { useRef, useState } from 'react';
import { formatLength } from '../utils/units';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import { CircleNotch as Loader2 } from 'phosphor-react';

// Simple procedural textures using canvas for realism
const createNoiseTexture = (baseColor = '#B0B0B0', dotColor = '#888888', density = 0.08) => {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, size, size);
  const dots = Math.floor(size * size * density * 0.002);
  for (let i = 0; i < dots; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = Math.random() * 1.2 + 0.3;
    ctx.fillStyle = Math.random() > 0.5 ? dotColor : '#999999';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  texture.anisotropy = 4;
  return texture;
};

const createAsphaltTexture = () => createNoiseTexture('#2C2C2C', '#5a5a5a', 0.12);
const createConcreteTexture = () => createNoiseTexture('#B0B0B0', '#8a8a8a', 0.08);

const getCoatedColor = (coating) => {
  switch (coating) {
    case 'galvanized': return '#C5C9CC';
    case 'painted': return '#1e3a8a'; // company blue tone
    case 'powder': return '#374151';
    case 'weathering': return '#8B5A2B';
    default: return '#708090';
  }
};

// Enhanced Bridge component with width support and better materials
const Bridge3DModel = ({ configuration }) => {
  const groupRef = useRef();
  const [scale] = useState(1);

  const { length, width, subModifiers = {} } = configuration;
  const bridgeLength = configuration.trussType === 'long' ? Math.max(length, 20) : length;
  const bridgeWidth = width || 12;

  const getMaterialColor = (material) => {
    const base = {
      steel: '#708090',
      concrete: '#A0A0A0',
      wood: '#8B4513',
      aluminum: '#C0C0C0'
    };
    if (material === 'steel') return getCoatedColor(subModifiers.coating);
    return base[material] || '#708090';
  };

  const getDeckingMaterial = () => {
    const decking = configuration.decking;
    if (decking === 'asphalt') return new THREE.MeshStandardMaterial({ color: '#2C2C2C', roughness: 0.95, metalness: 0.0, map: createAsphaltTexture() });
    if (decking === 'concrete') return new THREE.MeshStandardMaterial({ color: '#B0B0B0', roughness: 0.9, metalness: 0.0, map: createConcreteTexture() });
    if (decking === 'wood') return new THREE.MeshStandardMaterial({ color: '#8B5E3C', roughness: 0.8, metalness: 0.0 });
    return new THREE.MeshStandardMaterial({ color: '#708090', roughness: 0.6, metalness: 0.4 });
  };

  const getRailingColor = (railing) => {
    const colors = { cable: '#404040', vertical: '#505050', horizontal: '#606060', glass: '#E0E0E0' };
    return colors[railing] || '#404040';
  };

  const BridgeDeck = () => {
    const camberHeight = configuration.camber / 12;
    const deckMaterial = getDeckingMaterial();
    const deckClear = 0.4; // keep deck slightly inside truss lines
    const deckW = Math.max(bridgeWidth - deckClear, bridgeWidth * 0.9);

    if (configuration.decking === 'wood') {
      const planks = [];
      const plankWidth = 0.25;
      const numPlanks = Math.floor(bridgeLength / plankWidth);
      for (let i = 0; i < numPlanks; i++) {
        const x = (i - numPlanks / 2) * plankWidth;
        const t = x / (bridgeLength / 2);
        const camberAtX = camberHeight * (1 - t * t);
        planks.push(
          <mesh key={i} position={[x, 0.2 + camberAtX, 0]} castShadow receiveShadow>
            <boxGeometry args={[plankWidth - 0.02, 0.15, deckW]} />
            <primitive object={deckMaterial} attach="material" />
          </mesh>
        );
      }
      return <group>{planks}</group>;
    }
    const numSegments = Math.max(20, Math.floor(bridgeLength / 2));
    const segmentWidth = bridgeLength / numSegments;
    const segments = [];
    for (let i = 0; i < numSegments; i++) {
      const x = (i - numSegments / 2 + 0.5) * segmentWidth;
      const t = x / (bridgeLength / 2);
      const camberAtX = camberHeight * (1 - t * t);
      segments.push(
        <mesh key={i} position={[x, -0.05 + camberAtX, 0]} castShadow receiveShadow>
          <boxGeometry args={[segmentWidth, 0.3, deckW]} />
          <primitive object={deckMaterial} attach="material" />
        </mesh>
      );
    }
    return <group>{segments}</group>;
  };

  const TrussStructure = () => {
    const materialColor = getMaterialColor(configuration.material);
    const material = new THREE.MeshStandardMaterial({ color: materialColor, metalness: 0.85, roughness: 0.25 });

    const renderWarrenTruss = () => (
      <group>
        <mesh position={[0, 0, bridgeWidth / 2 - 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 0, -bridgeWidth / 2 + 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 2, bridgeWidth / 2 - 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 2, -bridgeWidth / 2 + 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        {Array.from({ length: Math.floor(bridgeLength / 4) }, (_, i) => (
          <group key={i}>
            <mesh position={[(i - Math.floor(bridgeLength / 8)) * 4, 1, bridgeWidth / 2 - 0.1]} rotation={[0, 0, i % 2 === 0 ? Math.PI / 4 : -Math.PI / 4]} castShadow>
              <boxGeometry args={[2.83, 0.15, 0.15]} />
              <primitive object={material} attach="material" />
            </mesh>
            <mesh position={[(i - Math.floor(bridgeLength / 8)) * 4, 1, -bridgeWidth / 2 + 0.1]} rotation={[0, 0, i % 2 === 0 ? Math.PI / 4 : -Math.PI / 4]} castShadow>
              <boxGeometry args={[2.83, 0.15, 0.15]} />
              <primitive object={material} attach="material" />
            </mesh>
          </group>
        ))}
      </group>
    );

    const renderPrattTruss = () => (
      <group>
        <mesh position={[0, 0, bridgeWidth / 2 - 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 0, -bridgeWidth / 2 + 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 2, bridgeWidth / 2 - 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 2, -bridgeWidth / 2 + 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        {Array.from({ length: Math.floor(bridgeLength / 4) + 1 }, (_, i) => {
          const xPos = (i - Math.floor(bridgeLength / 8)) * 4;
          return (
            <group key={i}>
              <mesh position={[xPos, 1, bridgeWidth / 2 - 0.1]} castShadow>
                <boxGeometry args={[0.15, 2, 0.15]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[xPos, 1, -bridgeWidth / 2 + 0.1]} castShadow>
                <boxGeometry args={[0.15, 2, 0.15]} />
                <primitive object={material} attach="material" />
              </mesh>
              {i < Math.floor(bridgeLength / 4) && (
                <>
                  <mesh position={[xPos + 2, 1, bridgeWidth / 2 - 0.1]} rotation={[0, 0, Math.atan2(2, 4)]} castShadow>
                    <boxGeometry args={[Math.sqrt(16 + 4), 0.12, 0.12]} />
                    <primitive object={material} attach="material" />
                  </mesh>
                  <mesh position={[xPos + 2, 1, -bridgeWidth / 2 + 0.1]} rotation={[0, 0, Math.atan2(2, 4)]} castShadow>
                    <boxGeometry args={[Math.sqrt(16 + 4), 0.12, 0.12]} />
                    <primitive object={material} attach="material" />
                  </mesh>
                </>
              )}
            </group>
          );
        })}
      </group>
    );

    const renderLongTruss = () => (
      <group>
        <mesh position={[0, 0, bridgeWidth / 2 - 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 0, -bridgeWidth / 2 + 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 2.5, bridgeWidth / 2 - 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        <mesh position={[0, 2.5, -bridgeWidth / 2 + 0.1]} castShadow receiveShadow>
          <boxGeometry args={[bridgeLength, 0.2, 0.2]} />
          <primitive object={material} attach="material" />
        </mesh>
        {Array.from({ length: Math.floor(bridgeLength / 3) }, (_, i) => (
          <group key={i}>
            <mesh position={[(i - Math.floor(bridgeLength / 6)) * 3, 1.25, bridgeWidth / 2 - 0.1]} rotation={[0, 0, i % 2 === 0 ? Math.PI / 4 : -Math.PI / 4]} castShadow>
              <boxGeometry args={[3.54, 0.15, 0.15]} />
              <primitive object={material} attach="material" />
            </mesh>
            <mesh position={[(i - Math.floor(bridgeLength / 6)) * 3, 1.25, -bridgeWidth / 2 + 0.1]} rotation={[0, 0, i % 2 === 0 ? Math.PI / 4 : -Math.PI / 4]} castShadow>
              <boxGeometry args={[3.54, 0.15, 0.15]} />
              <primitive object={material} attach="material" />
            </mesh>
          </group>
        ))}
      </group>
    );

    const renderModularTruss = () => {
      const moduleWidth = 4;
      const numModules = Math.max(3, Math.floor(bridgeLength / moduleWidth));
      const actualModuleWidth = bridgeLength / numModules;
      return (
        <group>
          {Array.from({ length: numModules }, (_, i) => (
            <group key={i} position={[(i - (numModules - 1) / 2) * actualModuleWidth, 0, 0]}>
              {/* Bottom chords */}
              <mesh position={[0, 0, bridgeWidth / 2 - 0.1]} castShadow receiveShadow>
                <boxGeometry args={[actualModuleWidth - 0.2, 0.2, 0.2]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[0, 0, -bridgeWidth / 2 + 0.1]} castShadow receiveShadow>
                <boxGeometry args={[actualModuleWidth - 0.2, 0.2, 0.2]} />
                <primitive object={material} attach="material" />
              </mesh>
              {/* Top chords */}
              <mesh position={[0, 2, bridgeWidth / 2 - 0.1]} castShadow receiveShadow>
                <boxGeometry args={[actualModuleWidth - 0.2, 0.2, 0.2]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[0, 2, -bridgeWidth / 2 + 0.1]} castShadow receiveShadow>
                <boxGeometry args={[actualModuleWidth - 0.2, 0.2, 0.2]} />
                <primitive object={material} attach="material" />
              </mesh>
              {/* Vertical posts */}
              <mesh position={[-actualModuleWidth/2 + 0.1, 1, bridgeWidth/2 - 0.1]} castShadow>
                <boxGeometry args={[0.15, 2, 0.15]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[actualModuleWidth/2 - 0.1, 1, bridgeWidth/2 - 0.1]} castShadow>
                <boxGeometry args={[0.15, 2, 0.15]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[-actualModuleWidth/2 + 0.1, 1, -bridgeWidth/2 + 0.1]} castShadow>
                <boxGeometry args={[0.15, 2, 0.15]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[actualModuleWidth/2 - 0.1, 1, -bridgeWidth/2 + 0.1]} castShadow>
                <boxGeometry args={[0.15, 2, 0.15]} />
                <primitive object={material} attach="material" />
              </mesh>
              {/* Cross braces */}
              <mesh position={[0, 1, bridgeWidth/2 - 0.1]} rotation={[0, 0, Math.atan2(2, actualModuleWidth)]} castShadow>
                <boxGeometry args={[Math.sqrt(actualModuleWidth * actualModuleWidth + 4), 0.12, 0.12]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[0, 1, bridgeWidth/2 - 0.1]} rotation={[0, 0, -Math.atan2(2, actualModuleWidth)]} castShadow>
                <boxGeometry args={[Math.sqrt(actualModuleWidth * actualModuleWidth + 4), 0.12, 0.12]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[0, 1, -bridgeWidth/2 + 0.1]} rotation={[0, 0, Math.atan2(2, actualModuleWidth)]} castShadow>
                <boxGeometry args={[Math.sqrt(actualModuleWidth * actualModuleWidth + 4), 0.12, 0.12]} />
                <primitive object={material} attach="material" />
              </mesh>
              <mesh position={[0, 1, -bridgeWidth/2 + 0.1]} rotation={[0, 0, -Math.atan2(2, actualModuleWidth)]} castShadow>
                <boxGeometry args={[Math.sqrt(actualModuleWidth * actualModuleWidth + 4), 0.12, 0.12]} />
                <primitive object={material} attach="material" />
              </mesh>
            </group>
          ))}
        </group>
      );
    };

    switch (configuration.trussType) {
      case 'pratt':
        return renderPrattTruss();
      case 'long':
        return renderLongTruss();
      case 'modular':
        return renderModularTruss();
      default:
        return renderWarrenTruss();
    }
  };

  // Floor system: cross floorbeams and longitudinal stringers under deck
  const FloorSystem = () => {
    const mat = new THREE.MeshStandardMaterial({ color: getMaterialColor(configuration.material), metalness: 0.8, roughness: 0.35 });
    const camberHeight = configuration.camber / 12;

    // Panel-based spacing aligned with truss nodes (~4 ft)
    const panel = 4;
    const count = Math.floor(bridgeLength / panel);
    const floorBeams = [];
    for (let i = 0; i <= count; i++) {
      const x = (i - count / 2) * panel;
      const t = x / (bridgeLength / 2);
      const camberAtX = camberHeight * (1 - t * t);
      floorBeams.push(
        <mesh key={`fb-${i}`} position={[x, -0.9 + camberAtX, 0]} castShadow>
          {/* span between truss lines */}
          <boxGeometry args={[0.35, 0.3, Math.max(bridgeWidth - 0.4, bridgeWidth * 0.9)]} />
          <primitive object={mat} attach="material" />
        </mesh>
      );
    }

    // Longitudinal stringers under deck, inside trusses
    // Weld stringers to truss bottoms: align z to near truss lines and y to bottom chord elevation
    // Use the existing bottom chords (lower truss chords) visually by aligning stringers precisely and minimizing their profile
    const stringerZ = [-(bridgeWidth/2 - 0.1), (bridgeWidth/2 - 0.1)];
    const stringers = stringerZ.map((z, idx) => (
      <mesh key={`str-${idx}`} position={[0, -0.99, z]} castShadow>
        <boxGeometry args={[bridgeLength, 0.12, 0.12]} />
        <primitive object={mat} attach="material" />
      </mesh>
    ));

    return <group>{floorBeams}{stringers}</group>;
  };

  // Proper Railing with different styles, width support, and camber following
  const Railing = () => {
    const railingColor = getRailingColor(configuration.railing);
    const camberHeight = configuration.camber / 12;
    const picketStyle = (subModifiers && subModifiers.picketStyle) || 'standard';

    const renderCableRailing = () => {
      const numPosts = Math.floor(bridgeLength / 6) + 1;
      return (
        <group>
          {Array.from({ length: numPosts }, (_, i) => {
            const x = (i - (numPosts - 1) / 2) * (bridgeLength / (numPosts - 1));
            const t = x / (bridgeLength / 2);
            const camberAtX = camberHeight * (1 - t * t);
            return (
              <group key={i}>
                <mesh position={[x, 0.5 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                  <boxGeometry args={[0.1, 1, 0.1]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                <mesh position={[x, 0.5 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                  <boxGeometry args={[0.1, 1, 0.1]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
              </group>
            );
          })}
          {Array.from({ length: 4 }, (_, cableLevel) => {
            const numCableSegments = Math.floor(bridgeLength / 2);
            return Array.from({ length: numCableSegments }, (_, i) => {
              const x = (i - numCableSegments / 2 + 0.5) * (bridgeLength / numCableSegments);
              const t = x / (bridgeLength / 2);
              const camberAtX = camberHeight * (1 - t * t);
              const cableY = 0.3 + cableLevel * 0.15 + camberAtX;
              return (
                <group key={`${cableLevel}-${i}`}>
                  <mesh position={[x, cableY, bridgeWidth / 2 + 0.1]} rotation={[0, 0, Math.PI / 2]}>
                    <cylinderGeometry args={[0.01, 0.01, bridgeLength / numCableSegments]} />
                    <meshStandardMaterial color={railingColor} metalness={0.8} roughness={0.25} />
                  </mesh>
                  <mesh position={[x, cableY, -bridgeWidth / 2 - 0.1]} rotation={[0, 0, Math.PI / 2]}>
                    <cylinderGeometry args={[0.01, 0.01, bridgeLength / numCableSegments]} />
                    <meshStandardMaterial color={railingColor} metalness={0.8} roughness={0.25} />
                  </mesh>
                </group>
              );
            });
          })}
        </group>
      );
    };

    const renderVerticalRailing = () => {
      const numSegments = Math.floor(bridgeLength / 2);
      return (
        <group>
          {Array.from({ length: numSegments }, (_, i) => {
            const x = (i - numSegments / 2 + 0.5) * (bridgeLength / numSegments);
            const t = x / (bridgeLength / 2);
            const camberAtX = camberHeight * (1 - t * t);
            const segmentLength = bridgeLength / numSegments;
            return (
              <group key={i}>
                <mesh position={[x, 0.9 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.08, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                <mesh position={[x, 0.9 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.08, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                <mesh position={[x, 0.1 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.08, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                <mesh position={[x, 0.1 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.08, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
              </group>
            );
          })}
          {Array.from({ length: Math.floor(bridgeLength * 2) }, (_, i) => {
            const x = (i - bridgeLength + 0.5) * 0.5;
            const t = x / (bridgeLength / 2);
            const camberAtX = camberHeight * (1 - t * t);
            return (
              <group key={i}>
                <mesh position={[x, 0.5 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                  <boxGeometry args={[0.04, 0.8, 0.04]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                {picketStyle === 'spear-top' && (
                  <mesh position={[x, 0.95 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                    <coneGeometry args={[0.06, 0.12, 8]} />
                    <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                  </mesh>
                )}
                <mesh position={[x, 0.5 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                  <boxGeometry args={[0.04, 0.8, 0.04]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                {picketStyle === 'spear-top' && (
                  <mesh position={[x, 0.95 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                    <coneGeometry args={[0.06, 0.12, 8]} />
                    <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                  </mesh>
                )}
              </group>
            );
          })}
        </group>
      );
    };

    const renderHorizontalRailing = () => {
      const numSegments = Math.floor(bridgeLength / 2);
      return (
        <group>
          {Array.from({ length: 5 }, (_, railLevel) => (
            Array.from({ length: numSegments }, (_, i) => {
              const x = (i - numSegments / 2 + 0.5) * (bridgeLength / numSegments);
              const t = x / (bridgeLength / 2);
              const camberAtX = camberHeight * (1 - t * t);
              const segmentLength = bridgeLength / numSegments;
              const railY = 0.2 + railLevel * 0.15 + camberAtX;
              return (
                <group key={`${railLevel}-${i}`}>
                  <mesh position={[x, railY, bridgeWidth / 2 + 0.1]} castShadow>
                    <boxGeometry args={[segmentLength, 0.06, 0.06]} />
                    <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                  </mesh>
                  <mesh position={[x, railY, -bridgeWidth / 2 - 0.1]} castShadow>
                    <boxGeometry args={[segmentLength, 0.06, 0.06]} />
                    <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                  </mesh>
                </group>
              );
            })
          ))}
          {Array.from({ length: Math.floor(bridgeLength / 6) + 1 }, (_, i) => {
            const x = (i - Math.floor(bridgeLength / 12)) * 6;
            const t = x / (bridgeLength / 2);
            const camberAtX = camberHeight * (1 - t * t);
            return (
              <group key={i}>
                <mesh position={[x, 0.5 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                  <boxGeometry args={[0.08, 1, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                <mesh position={[x, 0.5 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                  <boxGeometry args={[0.08, 1, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
              </group>
            );
          })}
        </group>
      );
    };

    const renderGlassRailing = () => {
      const numSegments = Math.floor(bridgeLength / 2);
      return (
        <group>
          {Array.from({ length: numSegments }, (_, i) => {
            const x = (i - numSegments / 2 + 0.5) * (bridgeLength / numSegments);
            const t = x / (bridgeLength / 2);
            const camberAtX = camberHeight * (1 - t * t);
            const segmentLength = bridgeLength / numSegments;
            return (
              <group key={i}>
                {/* top and bottom rails */}
                <mesh position={[x, 0.9 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.08, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                <mesh position={[x, 0.9 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.08, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                <mesh position={[x, 0.1 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.08, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                <mesh position={[x, 0.1 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.08, 0.08]} />
                  <meshStandardMaterial color={railingColor} metalness={0.7} roughness={0.35} />
                </mesh>
                {/* glass panels */}
                <mesh position={[x, 0.5 + camberAtX, bridgeWidth / 2 + 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.95, 0.04]} />
                  <meshPhysicalMaterial color={'#E0E0E0'} opacity={0.35} transparent transmission={0.95} roughness={0.05} />
                </mesh>
                <mesh position={[x, 0.5 + camberAtX, -bridgeWidth / 2 - 0.1]} castShadow>
                  <boxGeometry args={[segmentLength, 0.95, 0.04]} />
                  <meshPhysicalMaterial color={'#E0E0E0'} opacity={0.35} transparent transmission={0.95} roughness={0.05} />
                </mesh>
              </group>
            );
          })}
        </group>
      );
    };

    switch (configuration.railing) {
      case 'cable':
        return renderCableRailing();
      case 'vertical':
        return renderVerticalRailing();
      case 'horizontal':
        return renderHorizontalRailing();
      case 'glass':
        return renderGlassRailing();
      default:
        return renderCableRailing();
    }
  };

  const AddOns = () => {
    if (!configuration.addOns || configuration.addOns.length === 0) return null;
    return (
      <group>
        {configuration.addOns.includes('led-lighting') && (
          <group>
            <mesh position={[0, -0.2, bridgeWidth / 2 + 0.2]} castShadow>
              <boxGeometry args={[bridgeLength, 0.05, 0.05]} />
              <meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={0.6} metalness={0.6} roughness={0.4} />
            </mesh>
            <mesh position={[0, -0.2, -bridgeWidth / 2 - 0.2]} castShadow>
              <boxGeometry args={[bridgeLength, 0.05, 0.05]} />
              <meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={0.6} metalness={0.6} roughness={0.4} />
            </mesh>
          </group>
        )}
        {configuration.addOns.includes('safety-barriers') && (
          <group>
            {Array.from({ length: 8 }, (_, i) => (
              <mesh key={i} position={[(i - 3.5) * (bridgeLength / 8), 1.2, 0]} castShadow>
                <boxGeometry args={[0.2, 0.5, bridgeWidth + 0.5]} />
                <meshLambertMaterial color="#FF6B35" />
              </mesh>
            ))}
          </group>
        )}
      </group>
    );
  };

  const DeckEffects = () => {
    if (configuration.decking !== 'concrete' || !subModifiers?.concreteAdders?.glowstones) return null;
    const num = Math.max(20, Math.floor((bridgeLength * bridgeWidth) / 50));
    const dots = [];
    for (let i = 0; i < num; i++) {
      const x = (Math.random() - 0.5) * bridgeLength * 0.9;
      const z = (Math.random() - 0.5) * bridgeWidth * 0.9;
      const t = x / (bridgeLength / 2);
      const camberAtX = (configuration.camber / 12) * (1 - t * t);
      dots.push(
        <mesh key={i} position={[x, -0.18 + camberAtX, z]}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshStandardMaterial color="#9be7ff" emissive="#9be7ff" emissiveIntensity={1.2} toneMapped={false} />
        </mesh>
      );
    }
    return <group>{dots}</group>;
  };

  // Wingwall builder anchored at deck corner, oriented by direction vector in XZ plane
  const WingwallPair = ({ corner, dirX, dirZ, wallLen, color, matProps, showRailing, railingType, stopGap = 0.5 }) => {
    // corner: [x,y,z] at deck corner; dirX/dirZ define outward direction
    const [cx, cy, cz] = corner;
    const effLen = Math.max(0, wallLen - stopGap); // stop 6" short at bridge end
    const wallHeight = 1.2;
    const len = Math.hypot(dirX, dirZ) || 1;
    const nx = dirX / len;
    const nz = dirZ / len;
    // For yaw: forward vector after yaw is [sin(yaw), 0, cos(yaw)]
    const yaw = Math.atan2(nx, nz);

    const renderRailing = () => {
      if (!showRailing) return null;
      const xOut = 0.0; // top-center of wall
      const yTop = wallHeight + 0.05; // slightly above wall cap for visibility
      if (railingType === 'cable') {
        return (
          <group>
            {Array.from({ length: 4 }, (_, level) => (
              <mesh key={`wrc-${level}`} position={[xOut, yTop + level * 0.15, stopGap + effLen / 2]} rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.03, 0.03, effLen]} />
                <meshStandardMaterial color="#7a7a7a" metalness={0.8} roughness={0.25} polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
              </mesh>
            ))}
          </group>
        );
      }
      if (railingType === 'horizontal') {
        return (
          <group>
            {Array.from({ length: 4 }, (_, level) => (
              <mesh key={`wrh-${level}`} position={[xOut, yTop + level * 0.18, stopGap + effLen / 2]}>
                {/* along Z */}
                <boxGeometry args={[0.06, 0.06, effLen]} />
                <meshStandardMaterial color="#5a5a5a" polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
              </mesh>
            ))}
          </group>
        );
      }
      if (railingType === 'vertical') {
        const picketCount = Math.max(5, Math.floor(effLen / 0.5));
        return (
          <group>
            {/* Bottom bar */}
            <mesh position={[xOut, yTop, stopGap + effLen / 2]}>
              <boxGeometry args={[0.06, 0.06, effLen]} />
              <meshStandardMaterial color="#5a5a5a" polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
            </mesh>
            {/* Top bar */}
            <mesh position={[xOut, yTop + 0.8, stopGap + effLen / 2]}>
              <boxGeometry args={[0.06, 0.06, effLen]} />
              <meshStandardMaterial color="#5a5a5a" polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
            </mesh>
            {/* Vertical pickets */}
            {Array.from({ length: picketCount }, (_, i) => (
              <mesh key={`wrv-${i}`} position={[xOut, yTop + 0.4, stopGap + (i + 0.5) * (effLen / picketCount)]}>
                <boxGeometry args={[0.04, 0.8, 0.04]} />
                <meshStandardMaterial color="#5a5a5a" polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
              </mesh>
            ))}
          </group>
        );
      }
      if (railingType === 'glass') {
        return (
          <group>
            {/* Bottom bar */}
            <mesh position={[xOut, yTop, stopGap + effLen / 2]}>
              <boxGeometry args={[0.06, 0.06, effLen]} />
              <meshStandardMaterial color="#5a5a5a" polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
            </mesh>
            {/* Top bar */}
            <mesh position={[xOut, yTop + 0.8, stopGap + effLen / 2]}>
              <boxGeometry args={[0.06, 0.06, effLen]} />
              <meshStandardMaterial color="#5a5a5a" polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
            </mesh>
            {/* Glass panel only (no pickets) */}
            <mesh position={[xOut, yTop + 0.4, stopGap + effLen / 2]}>
              <boxGeometry args={[0.04, 0.95, effLen]} />
              <meshPhysicalMaterial color={'#E0E0E0'} opacity={0.35} transparent transmission={0.95} roughness={0.05} polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
            </mesh>
          </group>
        );
      }
      return null;
    };

    return (
      <group position={[cx, cy, cz]} rotation={[0, yaw, 0]}>
        {/* Wall body: start at stopGap from corner and extend outward */}
        <mesh position={[0, wallHeight / 2, stopGap + effLen / 2]} castShadow receiveShadow>
          <boxGeometry args={[1, wallHeight, effLen]} />
          <meshStandardMaterial color={color} {...matProps} />
        </mesh>
        {/* Matching railing along wall */}
        {renderRailing()}
      </group>
    );
  };

  return (
    <group ref={groupRef} scale={[scale, scale, scale]}>
      {/* Threshold plates and abutments with tiny reveal gap and beveled edges */}
      {(() => {
        const bw = bridgeWidth;
        const abutThk = 2;
        const bevel = 0.1;
        const decking = configuration.decking;
        const deckBottomY = decking === 'wood' ? -0.475 : -0.65; // near ends
        const gap = 0.03; // ~3/8" visual reveal
        const topY = deckBottomY - gap;
        const bottomY = -1.95; // slightly above ground plane (-2)
        const abutHt = Math.max(0.2, topY - bottomY);
        const centerY = (topY + bottomY) / 2;
        const abutDepth = bw + 2;
        const plateThk = 0.05; // ~5/8" threshold plate thickness
        const plateDepth = 1.0; // 12" deep plate
        const plateY = topY + plateThk/2; // sits slightly above abutment top, under deck
        return (
          <>
            {/* Upstream threshold plate */}
            <mesh position={[-(bridgeLength / 2) - 0.5 + abutThk/2 + 0.01, plateY, 0]} castShadow receiveShadow>
              <boxGeometry args={[plateThk, plateThk, Math.max(bw - 0.6, bw * 0.9)]} />
              <meshStandardMaterial color="#B8B8B8" roughness={0.5} metalness={0.3} />
            </mesh>
            {/* Downstream threshold plate */}
            <mesh position={[(bridgeLength / 2) + 0.5 - abutThk/2 - 0.01, plateY, 0]} castShadow receiveShadow>
              <boxGeometry args={[plateThk, plateThk, Math.max(bw - 0.6, bw * 0.9)]} />
              <meshStandardMaterial color="#B8B8B8" roughness={0.5} metalness={0.3} />
            </mesh>

            <RoundedBox args={[abutThk, abutHt, abutDepth]} radius={bevel} smoothness={2} position={[-(bridgeLength / 2) - 0.5, centerY, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#A9A9A9" roughness={0.95} metalness={0.0} />
            </RoundedBox>
            <RoundedBox args={[abutThk, abutHt, abutDepth]} radius={bevel} smoothness={2} position={[(bridgeLength / 2) + 0.5, centerY, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#A9A9A9" roughness={0.95} metalness={0.0} />
            </RoundedBox>
          </>
        );
      })()}

      {/* Deck, Structure, Floor system and Railings */}
      <BridgeDeck />
      <FloorSystem />
      <TrussStructure />
      <Railing />
      <AddOns />
      <DeckEffects />

      {/* Scenery and wingwalls */}
      {(() => {
        const sub = configuration?.subModifiers || {};
        const wing = sub.wingwalls || 'none';
        if (wing === 'none') return null;
        const wingColor = wing === 'stone' ? '#8B4513' : wing === 'steel-sheet' ? '#708090' : '#A0A0A0';
        // Use custom length if provided, else auto by width up to 24ft
        const customLenFt = sub.wingwallLengthFt;
        const wallLen = customLenFt ? customLenFt : Math.min(bridgeWidth + 6, 24);
        const wallHt = 1.2;
        const y = -2.0 + 0.0; // corner y at ground; WingwallPair will set height
        const deckXUp = -(bridgeLength / 2);
        const deckXDn = (bridgeLength / 2);
        const deckZEdge = (bridgeWidth / 2);
        const startOff = 0.0; // corner exactly at deck edge
        const cornerUpL = [deckXUp, y,  deckZEdge];
        const cornerUpR = [deckXUp, y, -deckZEdge];
        const cornerDnL = [deckXDn, y,  deckZEdge];
        const cornerDnR = [deckXDn, y, -deckZEdge];
        const matProps = { roughness: 0.9, metalness: wing === 'steel-sheet' ? 0.7 : 0.0 };
        const showRailing = !!sub.wingwallRailings;
        const railingType = configuration.railing;
        // Build outward direction vectors from deck center: always away from bridge center
        const outwardDir = (corner) => {
          const [x, , z] = corner;
          const sideSign = z >= 0 ? 1 : -1;   // +Z left, -Z right
          const endSign = x >= 0 ? 1 : -1;    // +X downstream, -X upstream
          const vx = endSign * 0.6;           // slight along X away from center
          const vz = sideSign * 1.0;          // mostly outwards in Z
          return [vx, 0, vz];
        };

        const dUpL = outwardDir(cornerUpL);
        const dUpR = outwardDir(cornerUpR);
        const dDnL = outwardDir(cornerDnL);
        const dDnR = outwardDir(cornerDnR);

        return (
          <group>
            {/* Upstream two walls anchored at deck corners */}
            <WingwallPair corner={cornerUpL} dirX={dUpL[0]} dirZ={dUpL[2]} wallLen={wallLen} color={wingColor} matProps={matProps} showRailing={showRailing} railingType={railingType} />
            <WingwallPair corner={cornerUpR} dirX={dUpR[0]} dirZ={dUpR[2]} wallLen={wallLen} color={wingColor} matProps={matProps} showRailing={showRailing} railingType={railingType} />
            {/* Downstream two walls anchored at deck corners */}
            <WingwallPair corner={cornerDnL} dirX={dDnL[0]} dirZ={dDnL[2]} wallLen={wallLen} color={wingColor} matProps={matProps} showRailing={showRailing} railingType={railingType} />
            <WingwallPair corner={cornerDnR} dirX={dDnR[0]} dirZ={dDnR[2]} wallLen={wallLen} color={wingColor} matProps={matProps} showRailing={showRailing} railingType={railingType} />
          </group>
        );
      })()}

      {/* Ground/Grass, River, Banks, Trees, Hills, Clouds */}
      <group>
        <mesh position={[0, -2.5, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[200, 200]} />
          <meshLambertMaterial color="#4a7c59" />
        </mesh>
        <mesh position={[0, -2.4, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[20, 200]} />
          <meshLambertMaterial color="#4682B4" opacity={0.8} transparent />
        </mesh>
        <mesh position={[10, -2.35, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[1, 200]} />
          <meshLambertMaterial color="#8B4513" />
        </mesh>
        <mesh position={[-10, -2.35, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[1, 200]} />
          <meshLambertMaterial color="#8B4513" />
        </mesh>
        {Array.from({ length: 8 }, (_, i) => {
          const x = (i - 4) * 15 + (Math.random() - 0.5) * 5;
          const z = (i % 2 === 0 ? 25 : -25) + (Math.random() - 0.5) * 10;
          return (
            <group key={i} position={[x, -2, z]}>
              <mesh position={[0, 2, 0]} castShadow>
                <cylinderGeometry args={[0.3, 0.5, 4]} />
                <meshLambertMaterial color="#8B4513" />
              </mesh>
              <mesh position={[0, 5, 0]} castShadow>
                <sphereGeometry args={[2.5]} />
                <meshLambertMaterial color="#228B22" />
              </mesh>
            </group>
          );
        })}
        {Array.from({ length: 5 }, (_, i) => {
          const x = (i - 2) * 40;
          const z = -60 - Math.random() * 20;
          const s = 0.5 + Math.random() * 0.5;
          return (
            <mesh key={i} position={[x, -2 + s * 3, z]} receiveShadow>
              <sphereGeometry args={[8 * s, 8, 6]} />
              <meshLambertMaterial color="#6B8E23" />
            </mesh>
          );
        })}
        {Array.from({ length: 6 }, (_, i) => {
          const x = (i - 3) * 25 + (Math.random() - 0.5) * 10;
          const y = 15 + Math.random() * 5;
          const z = -30 - Math.random() * 20;
          return (
            <group key={i} position={[x, y, z]}>
              <mesh castShadow>
                <sphereGeometry args={[3, 8, 6]} />
                <meshLambertMaterial color="#F0F8FF" opacity={0.9} transparent />
              </mesh>
              <mesh position={[2, 0, 0]} castShadow>
                <sphereGeometry args={[2.5, 8, 6]} />
                <meshLambertMaterial color="#F0F8FF" opacity={0.9} transparent />
              </mesh>
              <mesh position={[-2, 0, 0]} castShadow>
                <sphereGeometry args={[2, 8, 6]} />
                <meshLambertMaterial color="#F0F8FF" opacity={0.9} transparent />
              </mesh>
            </group>
          );
        })}
      </group>
    </group>
  );
};

export const BridgeViewer3D = ({ configuration, isGenerating, units = 'imperial' }) => {
  return (
    <div className="relative w-full h-full bg-gradient-to-b from-slate-800 to-slate-900 rounded-lg overflow-hidden">
      {isGenerating && (
        <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center z-10">
          <div className="flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
            <span className="text-slate-200 font-medium">Updating 3D Model...</span>
          </div>
        </div>
      )}

      <Canvas camera={{ position: [25, 12, 25], fov: 50 }} style={{ width: '100%', height: '100%' }} shadows>
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 15, 5]} intensity={1.2} castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} />
        <directionalLight position={[-5, 10, 10]} intensity={0.6} color="#FFF8DC" />
        <color attach="background" args={['#87CEEB']} />
        <Bridge3DModel configuration={configuration} />
        <OrbitControls enablePan enableZoom enableRotate maxPolarAngle={Math.PI / 2} minDistance={10} maxDistance={100} />
        <Text position={[0, 4, 0]} fontSize={1} color="#60a5fa" anchorX="center" anchorY="middle">
          {configuration.trussType.toUpperCase()} BRIDGE - {formatLength(configuration.length, units)} × {formatLength(configuration.width || 12, units)}
        </Text>
      </Canvas>

      <img src="https://customer-assets.emergentagent.com/job_truss-visualizer/artifacts/i3wf931s_2020%20365%20Royal%20Blue%20Logo.png" alt="365 Industrial Solutions" className="absolute bottom-4 left-4 opacity-30 w-24 h-auto pointer-events-none select-none" />
      <div className="absolute bottom-4 right-4 bg-slate-800/90 border border-slate-600 rounded-lg p-2 text-xs text-slate-300">
        <p>🖱️ Drag to rotate • 🔄 Scroll to zoom • ⇄ Right-click to pan</p>
      </div>
    </div>
  );
};